import React from 'react';
import { Folder, File, FileCode, FileImage, FileText, Calendar, HardDrive } from 'lucide-react';
import { formatFileSize, formatDate, highlightText } from '../utils/fileUtils';

const FileItem = ({ item, isFolder, onNavigate, searchQuery }) => {
  const GITHUB_OWNER = process.env.REACT_APP_GITHUB_OWNER || '';
  const GITHUB_REPO = process.env.REACT_APP_GITHUB_REPO || '';
  const GITHUB_URL = `https://github.com/${GITHUB_OWNER}/${GITHUB_REPO}`;
  const REPO_BRANCH = 'main';

  const getFileIcon = (lang) => {
    const iconMap = {
      'java': { Icon: FileCode, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-950' },
      'c': { Icon: FileCode, color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-950' },
      'cpp': { Icon: FileCode, color: 'text-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-950' },
      'py': { Icon: FileCode, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950' },
      'js': { Icon: FileCode, color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-950' },
      'html': { Icon: FileCode, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-950' },
      'css': { Icon: FileCode, color: 'text-cyan-600', bg: 'bg-cyan-50 dark:bg-cyan-950' },
      'png': { Icon: FileImage, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-950' },
      'jpg': { Icon: FileImage, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-950' },
      'jpeg': { Icon: FileImage, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-950' },
      'gif': { Icon: FileImage, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-950' },
      'pdf': { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950' },
      'txt': { Icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50 dark:bg-slate-800' },
      'md': { Icon: FileText, color: 'text-slate-700', bg: 'bg-slate-100 dark:bg-slate-800' }
    };

    return iconMap[lang?.toLowerCase()] || { Icon: File, color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800' };
  };

  if (isFolder) {
    return (
      <div
        onClick={() => onNavigate(item.path)}
        className="group bg-gradient-to-br from-amber-50 to-white dark:from-amber-950 dark:to-slate-800 border border-amber-200 dark:border-amber-900 rounded-xl p-5 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-amber-400 dark:hover:border-amber-600"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center flex-shrink-0">
            <Folder className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 dark:text-slate-100 mb-1 truncate">{item.name}</div>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <Folder className="w-3 h-3" />
              <span>Folder</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fileName = searchQuery ?
    <span dangerouslySetInnerHTML={{ __html: highlightText(item.name, searchQuery) }} /> :
    item.name;

  const { Icon, color, bg } = getFileIcon(item.lang);
  const githubUrl = `${GITHUB_URL}/blob/${REPO_BRANCH}/${item.path}${item.name}`;

  const getBadgeStyles = (type) => {
    const styles = {
      'DT': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'DC': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'PRIME': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'TRAINING': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'MNC': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'CONCEPT': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
    };
    return styles[type] || '';
  };

  const typeBadge = item.type && item.type !== 'file' && item.type !== 'OTHER' ? (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${getBadgeStyles(item.type)}`}>
      {item.type}
    </span>
  ) : null;

  const fileSize = item.size ? formatFileSize(item.size) : '';

  return (
    <a
      href={githubUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-600 block"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100 mb-1 line-clamp-2 flex items-start justify-between gap-2">
            <span className="flex-1">{fileName}</span>
            {typeBadge}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
            <span className="flex items-center gap-1">
              <FileCode className="w-3 h-3" />
              {item.lang.toUpperCase()}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(item.date)}
            </span>
            {fileSize && (
              <span className="flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {fileSize}
              </span>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

export default FileItem;
